import { post } from "./client";
import { type Nominal, type OkResponse, type TypedUrl, url } from "./common";
import type { ProfileImageData, UserId } from "./user";
import { workshopModels, type WorkshopId } from "./workshop";

export type SmallTeamId = Nominal<string, "SmallTeam">;

// ---- Entity ----

// Bag of optional seed values for the team. Currently only `formData` is read
// by the backend (spread into a fresh showroom entry on creation). No code path
// writes `meta` — values are seeded externally / historically.
// This seems like a weird design decision. Worth investigating
export type SmallTeamMeta = {
  formData?: Record<string, unknown>;
};

export type SmallTeam = {
  id: SmallTeamId;
  name: string;
  workshopId: WorkshopId;
  createdAt: Date;
  meta: SmallTeamMeta | null;
};

// ---- Response shapes ----

// Returned by GET /small-team?workshopId — the requesting user's small team in the given workshop.
export type SmallTeamForUser = Pick<SmallTeam, "id" | "name" | "meta">;

// Item in GET /workshops/:id/small-teams.
export type WorkshopSmallTeam = Omit<SmallTeam, "meta">;

// Item in GET /small-team/:id/members — subset of User (no createdAt, no
// email/conversionChannel, no isAdmin: roster is shown on the public showroom).
export type SmallTeamMember = {
  id: UserId;
  tag: number;
  name: string;
  profileImageData: ProfileImageData | null;
  lastActionAt: Date;
};

// ---- Models ----

// SMALL_TEAM has two URL forms with different response shapes: pass `smallTeamId`
// for direct lookup (returns full SmallTeam) or `workshopId` for the requesting
// user's team in that workshop (returns the lighter SmallTeamForUser shape).
export const smallTeamModels = {
  SMALL_TEAM: ((opts: {
    workshopId?: WorkshopId;
    smallTeamId?: SmallTeamId;
  }) =>
    opts.smallTeamId
      ? url<SmallTeam>(`/small-team/${opts.smallTeamId}`)
      : url<SmallTeamForUser>(`/small-team?workshopId=${opts.workshopId}`)) as {
    (opts: {
      workshopId: WorkshopId;
      smallTeamId?: never;
    }): TypedUrl<SmallTeamForUser>;
    (opts: {
      smallTeamId: SmallTeamId;
      workshopId?: never;
    }): TypedUrl<SmallTeam>;
  },
  SMALL_TEAM_MEMBERS: (smallTeamId: SmallTeamId) =>
    url<SmallTeamMember[]>(`/small-team/${smallTeamId}/members`),
  WORKSHOP_SMALL_TEAMS: (workshopId: WorkshopId) =>
    url<WorkshopSmallTeam[]>(`/workshops/${workshopId}/small-teams`),
};

// ---- Mutations ----

type SmallTeamMembershipInput = {
  workshopId: WorkshopId;
  userId: UserId;
  smallTeamId: SmallTeamId;
};

type UpdateSmallTeamInfoInput = {
  workshopId: WorkshopId;
  smallTeamId: SmallTeamId;
  name: string;
};

export const smallTeamMutations = {
  createSmallTeam: ({
    workshopId,
    name,
  }: {
    workshopId: WorkshopId;
    name: string;
  }) =>
    post<OkResponse>(`/workshop/${workshopId}/small-team`, { name }, [
      smallTeamModels.WORKSHOP_SMALL_TEAMS(workshopId),
    ]),
  addUserToSmallTeam: ({
    workshopId,
    userId,
    smallTeamId,
  }: SmallTeamMembershipInput) =>
    post<OkResponse>(`/workshops/add-to-small-team`, { userId, smallTeamId }, [
      workshopModels.WORKSHOP_USER_INFO(workshopId),
    ]),
  deleteUserFromSmallTeam: ({
    workshopId,
    userId,
    smallTeamId,
  }: SmallTeamMembershipInput) =>
    post<OkResponse>(
      `/workshops/delete-from-small-team`,
      { userId, smallTeamId },
      [workshopModels.WORKSHOP_USER_INFO(workshopId)],
    ),
  updateSmallTeamInfo: ({
    workshopId,
    smallTeamId,
    name,
  }: UpdateSmallTeamInfoInput) =>
    post<SmallTeam>(`/small-team/${smallTeamId}`, { name }, [
      smallTeamModels.WORKSHOP_SMALL_TEAMS(workshopId),
    ]),
  deleteSmallTeam: ({
    workshopId,
    smallTeamId,
  }: {
    workshopId: WorkshopId;
    smallTeamId: SmallTeamId;
  }) =>
    post<OkResponse>(`/team/${smallTeamId}/delete`, {}, [
      workshopModels.WORKSHOP_USER_INFO(workshopId),
      smallTeamModels.WORKSHOP_SMALL_TEAMS(workshopId),
    ]),
};
